import { PopoverPlacement } from "@idui/react-popover";
import { AccessTime, Timelapse } from "@mui/icons-material";
import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AccessControl, FramerTooltip } from "@ryde/controls";
import {
  IRideDto,
  onActivateKeyDown,
  useDrawers,
  useHasPermissions,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { TabsBottomInfoItemStyled } from "../../../../right";
import {
  HeaderLine2SupplierShowIconStyled,
  RideHeaderSupplierTriggerStyled,
} from "./ride-header.styled";
import {
  buildRideSupplierHeaderTooltipContent,
  resolveRideSupplierSendStatus,
} from "./ride-supplier-header.utils";
import { RideSupplierSendStatusIcon } from "./ride-supplier-send-status-icon";

interface IRideHeaderSupplierInfoProps {
  rideDto: Partial<IRideDto>;
}

export const RideHeaderSupplierInfo: FC<IRideHeaderSupplierInfoProps> = ({
  rideDto,
}) => {
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { hasPermissions: viewSupplierPermission } = useHasPermissions({
    entity: "supplier",
    action: "read",
  });
  const { hasPermissionAllowSendBelowMinimum } = useHaveCustomPermissions();

  const supplierHasFreezeTime = rideDto.supplierHasFreezeTime;
  const rideHasPreservedTiming = rideDto.metadata?.preserveStationsTiming;
  const supplierSendStatus = resolveRideSupplierSendStatus({
    seatsCount: rideDto.metadata?.seatsCount,
    minimumPassengers: rideDto.rideTypeMinimumPassengersCountForSent,
    canSendBelowMinimumByPermission: hasPermissionAllowSendBelowMinimum,
  });
  const supplierTooltipContent = useMemo(
    () =>
      buildRideSupplierHeaderTooltipContent({
        rideDto,
        t,
        canSendBelowMinimumByPermission: hasPermissionAllowSendBelowMinimum,
      }),
    [rideDto, t, hasPermissionAllowSendBelowMinimum],
  );

  const handleSupplierClick = (): void => {
    const supplierId = rideDto.supplierId;

    if (viewSupplierPermission && supplierId) {
      openEditFormDrawer({
        entity: "supplier",
        id: supplierId,
      });
    }
  };

  return (
    <AccessControl entity="ride" action="view_ride_supplier">
      <TabsBottomInfoItemStyled className="ride-header-line2-supplier">
        <div className="item__wrapper">
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={supplierTooltipContent}
          >
            <RideHeaderSupplierTriggerStyled>
              {supplierHasFreezeTime && (
                <HeaderLine2SupplierShowIconStyled className="ride-header-supplier-clock">
                  <AccessTime fontSize="small" />
                </HeaderLine2SupplierShowIconStyled>
              )}
              <div className="item__content truncate-text">
                <label className="label">{t("supplier.single")}</label>
                <span
                  data-e2e="ride-supplier"
                  className={clsx({
                    info: true,
                    pointer: viewSupplierPermission,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={handleSupplierClick}
                  onKeyDown={onActivateKeyDown(handleSupplierClick)}
                >
                  {rideDto.supplierName || t("general.NO_NAME")}
                </span>
              </div>
              <RideSupplierSendStatusIcon status={supplierSendStatus} />
            </RideHeaderSupplierTriggerStyled>
          </FramerTooltip>
          {!!rideHasPreservedTiming && (
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={t("ride.preserveStationsTiming")}
            >
              <HeaderLine2SupplierShowIconStyled>
                <Timelapse fontSize="small" />
              </HeaderLine2SupplierShowIconStyled>
            </FramerTooltip>
          )}
        </div>
      </TabsBottomInfoItemStyled>
    </AccessControl>
  );
};
